/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import validate from 'validate.js';
import { DialogDefaultV2, SwitchV2, UsuarioSelectV2 } from '../../../components/v2';
import * as Role from '../../../constants/role';

const EMPTY_SUMMARY = {
  withCount: 0,
  withoutCount: 0,
  withLabels: [],
  withoutLabels: [],
};
const PREVIEW_LIMIT = 3;

const groupBoxSx = {
  p: 1.25,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  height: '100%',
};

const NsirGrupoCard = ({ title, labels }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!labels || labels.length === 0) return null;

  const preview = labels.slice(0, PREVIEW_LIMIT);
  const hasMore = labels.length > PREVIEW_LIMIT;

  return (
    <Box sx={groupBoxSx}>
      <Stack spacing={0.75}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip size="small" label={labels.length} />
        </Stack>
        <Stack spacing={0.25}>
          {preview.map((label, index) => (
            <Typography
              key={`${label}-${index}`}
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.4, wordBreak: 'break-word' }}
            >
              {label}
            </Typography>
          ))}
        </Stack>
        {hasMore && (
          <Box>
            <Button
              variant="text"
              size="small"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
            >
              Ver todas
            </Button>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 1.5, maxWidth: 360, maxHeight: 280, overflow: 'auto' }}>
                <Stack spacing={0.5}>
                  {labels.map((label, index) => (
                    <Typography key={`${label}-all-${index}`} variant="body2">
                      {label}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Popover>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

NsirGrupoCard.propTypes = {
  title: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
};

NsirGrupoCard.defaultProps = {
  labels: [],
};

const LiberarDigitacaoAntecipadaLoteModal = ({
  open,
  handlerCancel,
  handlerConfirm,
  summary,
  validationMessage,
  confirmDisabled,
}) => {
  const withCount = summary?.withCount || 0;
  const withoutCount = summary?.withoutCount || 0;
  const withLabels = summary?.withLabels || [];
  const withoutLabels = summary?.withoutLabels || [];
  const mustPickDigitador = withoutCount > 0;
  const canKeepExisting = withCount > 0;
  const bothGroups = withCount > 0 && withoutCount > 0;

  const [trocarTodos, setTrocarTodos] = useState(false);
  const [confirmTrocarOpen, setConfirmTrocarOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({ usuario: null });

  useEffect(() => {
    if (!open) {
      setConfirmTrocarOpen(false);
      return;
    }
    setErrors({});
    setValues({ usuario: null });
    setTrocarTodos(!canKeepExisting);
    setConfirmTrocarOpen(false);
  }, [open, canKeepExisting]);

  const showSelect = trocarTodos || mustPickDigitador;
  const usuarioRequired = trocarTodos || mustPickDigitador;

  const selectLabel = trocarTodos
    ? 'Digitador para todas as NSIRs'
    : 'Digitador para as NSIRs sem vínculo';

  const willReplaceExisting = trocarTodos && canKeepExisting;

  const onCancelLocal = () => {
    setErrors({});
    setValues({ usuario: null });
    setTrocarTodos(false);
    setConfirmTrocarOpen(false);
    handlerCancel();
  };

  const submitLiberar = () => {
    handlerConfirm({
      trocarTodos: trocarTodos || !canKeepExisting,
      digitadorId: values.usuario,
    });
  };

  const onConfirmLocal = () => {
    if (confirmDisabled) return;
    const schema = usuarioRequired
      ? { usuario: { presence: { allowEmpty: false, message: 'é obrigatório' } } }
      : {};
    const validationErrors = validate(values, schema);
    setErrors(validationErrors || {});
    if (validationErrors && Object.keys(validationErrors).length > 0) return;
    if (willReplaceExisting) {
      setConfirmTrocarOpen(true);
      return;
    }
    submitLiberar();
  };

  return (
    <>
    <DialogDefaultV2
      open={open}
      handlerCancel={onCancelLocal}
      handlerConfirm={onConfirmLocal}
      title="Liberar digitação antecipada em lote"
      confirmMessage="Liberar"
      disabledConfirmButton={confirmDisabled}
      maxWidth="md"
    >
      <Grid container spacing={1} sx={{ pt: 1 }}>
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning">
            A inspeção continua no campo porém o digitador vai conseguir digitar.
          </Alert>
        </Grid>
        {withCount > 0 && (
          <Grid size={{ xs: 12, sm: bothGroups ? 6 : 12 }}>
            <NsirGrupoCard title="Com digitador" labels={withLabels} />
          </Grid>
        )}
        {withoutCount > 0 && (
          <Grid size={{ xs: 12, sm: bothGroups ? 6 : 12 }}>
            <NsirGrupoCard title="Sem digitador" labels={withoutLabels} />
          </Grid>
        )}
        {canKeepExisting && (
          <Grid size={{ xs: 12 }}>
            <SwitchV2
              label="Trocar o digitador de todas as NSIRs selecionadas"
              checked={trocarTodos}
              handleChange={(event) => setTrocarTodos(event.target.checked)}
            />
          </Grid>
        )}
        {showSelect && (
          <Grid size={{ xs: 12 }}>
            <UsuarioSelectV2
              grupos={[Role.ADMIN, Role.DIGITADOR]}
              isRequired={usuarioRequired}
              ativo
              label={selectLabel}
              error={!!(errors && errors.usuario)}
              onSelect={(usuario) => setValues({ usuario })}
              value={values.usuario}
            />
          </Grid>
        )}
        {validationMessage && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">{validationMessage}</Alert>
          </Grid>
        )}
      </Grid>
    </DialogDefaultV2>
    <DialogDefaultV2
      open={confirmTrocarOpen}
      handlerCancel={() => setConfirmTrocarOpen(false)}
      handlerConfirm={() => {
        setConfirmTrocarOpen(false);
        submitLiberar();
      }}
      title="Confirmar troca de digitador"
      cancelMessage="Voltar"
      confirmMessage="Trocar mesmo assim"
      confirmColor="error"
      maxWidth="sm"
      fullWidth
      useFullScreen={false}
    >
      <Alert severity="error">
        Isso substitui o digitador atual de {withCount} {withCount === 1 ? 'NSIR' : 'NSIRs'} que já {withCount === 1 ? 'tem' : 'têm'} vínculo. O digitador anterior deixa de ter {withCount === 1 ? 'essa inspeção' : 'essas inspeções'}.
      </Alert>
    </DialogDefaultV2>
    </>
  );
};

LiberarDigitacaoAntecipadaLoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handlerCancel: PropTypes.func.isRequired,
  handlerConfirm: PropTypes.func.isRequired,
  summary: PropTypes.shape({
    withCount: PropTypes.number,
    withoutCount: PropTypes.number,
    withLabels: PropTypes.arrayOf(PropTypes.string),
    withoutLabels: PropTypes.arrayOf(PropTypes.string),
  }),
  validationMessage: PropTypes.string,
  confirmDisabled: PropTypes.bool,
};

LiberarDigitacaoAntecipadaLoteModal.defaultProps = {
  summary: EMPTY_SUMMARY,
  validationMessage: null,
  confirmDisabled: false,
};

export default LiberarDigitacaoAntecipadaLoteModal;
